import { Controller, type UseFormReturn } from "react-hook-form";
import { CheckboxWithLabel } from "../../common/components/CheckboxWithLabel";
import { SimpleSelect } from "../../common/components/Select/SimpleSelect";
import { useEffect } from "react";
import { type AffiliatePartnerData } from "./models";
import { Section, SectionTitle } from "../../MyAccount/components/common";
import { useTranslation } from "react-i18next";

export interface AffilateForm {
  id: string;
  subsidiary: string;
}

export const AFFILIATE_FORM_KEY = "affiliate";

type FormWithAffiliate = {
  [AFFILIATE_FORM_KEY]?: AffilateForm;
};

interface SalesPartnerSectionProps {
  affiliates: AffiliatePartnerData[];
  initialAffiliate?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: UseFormReturn<any>["control"];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormReturn<any>["register"];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormReturn<any>["setValue"];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  watch: UseFormReturn<any>["watch"];
}

export const AffiliateFormSection = ({
  control,
  register,
  setValue,
  watch,
  affiliates,
  initialAffiliate,
}: SalesPartnerSectionProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    register("affiliate.id");
  }, [register]);

  const makeCboxProps = (curValue: string, cboxValue: string | undefined) => ({
    checked: curValue === cboxValue,
    onCheck: () => {
      const newValue = curValue === cboxValue ? "" : cboxValue ?? "";
      setValue("affiliate.id", newValue);
      setValue("affiliate.subsidiary", "");
    },
  });

  const selectedAffiliateId = (watch("affiliate.id") ?? "") as string;
  const selectedAffiliate = affiliates.find((affiliate) => affiliate.affiliateId === selectedAffiliateId);

  return (
    <Section>
      <SectionTitle>{t("form.preferredSalesPartner.sectionTitle")}</SectionTitle>
      {affiliates.map((affiliate) => (
        <CheckboxWithLabel key={affiliate.affiliateId} {...makeCboxProps(selectedAffiliateId, affiliate.affiliateId)}>
          {t("form.preferredSalesPartner.cboxLabel", { salesPartnerName: affiliate.bannerName })}
        </CheckboxWithLabel>
      ))}

      {selectedAffiliate && selectedAffiliate.subsidiaries.length > 0 && (
        <Controller
          render={({ field: { value, onChange } }) => (
            <SimpleSelect
              values={selectedAffiliate.subsidiaries}
              value={value}
              onChange={onChange}
              label={t("form.preferredSalesPartner.dropdownLabel", { salesPartnerName: selectedAffiliate.bannerName })}
              fullWidth={true}
              data-test-id={"complete-registration-affiliate-subsidiary-saved"}
            />
          )}
          name={"affiliate.subsidiary"}
          control={control}
          defaultValue={initialAffiliate}
        />
      )}
    </Section>
  );
};
